import './index.less';

import commonMsg from 'messages';
import loginMsg from 'messages/Login';
import { Button, Divider, Icon, Input } from '@beehive/components';
import PropTypes from 'prop-types';
import React, { ChangeEvent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { isBlank, ajax } from 'utils';
import Captcha from './Captcha';
import * as actions from 'modules/actions/account';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import moment from 'moment';
import { encryptPassword } from 'utils/encryptPassword';
import { getOuterTitle } from '@mpc/common/src/utils';

const messages = { ...commonMsg, ...loginMsg };
export interface IpLoginProps extends RouteComponentProps {
  prefixCls?: string;
  ipCooperationSessionsLogin: Function;
  getUserInfo: Function;
}

/**
 * IP 版权方登录页（FR-11）— 派生自供应商登录页 Login/index.tsx
 *
 * <p>字段 = 账号 + 密码 + 易盾验证码；标题 = getOuterTitle()（IP 标题）；
 * <b>不提供</b>忘记密码/重置密码/供应商选择入口（本期后端不做，直接不做而非隐藏，FR-11）。
 * 提交调版权方登录接口 {@code ipCooperationSessionsLogin}（sessions）。</p>
 * <p>样式复用现有 index.less（Less 冻结，不新增 Less 文件）。</p>
 */
class IpLogin extends React.Component<IpLoginProps, any> {
  private refreshRef: any;
  public static contextTypes = {
    intl: PropTypes.object,
  };
  public static defaultProps = {
    prefixCls: 'mpc-external-login',
  };
  constructor(props: IpLoginProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }
  public handleLogin = () => {
    const { intl } = this.context;
    const { ipCooperationSessionsLogin, getUserInfo, history } = this.props;
    const { account, password, verifyCode } = this.state;
    const alertMsg = this.getAlertMsg();
    if (alertMsg) {
      return;
    }
    const body = {
      login: account,
      password: encryptPassword(password),
      _rucaptcha: verifyCode,
      encrypted: true,
    };
    this.setState({
      loading: true,
    });
    ipCooperationSessionsLogin(body).then(async (res: any) => {
      if (res.res_code === 1) {
        // 版权方无供应商选择，登录成功直接取用户信息进入工作台
        // 先 await getUserInfo（fetchPromise middleware 返回 actionPromise 可 await）确保 currentUser 就绪再跳转，
        // 否则 Main 组件 IP 场景在 !currentUser 时会 Redirect 回 /login（首次登录不跳首页、二次才跳的根因）
        await getUserInfo({});
        history.push('/');
      } else if (res.res_code === 0) {
        this.refreshRef && this.refreshRef({
          locale: intl.locale,
          onChange: this.handleChange,
        });
        this.setState({ alertMsg: res.res_msg, verifyCode: null, loading: false });
      }
      this.setState({
        loading: false,
      });
    }).catch(() => {
      this.setState({
        loading: false,
      });
    });
  }
  public handleChange = (newVal: { [key: string]: any }) => {
    const { errorKey, alertMsg } = this.state;
    let newErrorKey = errorKey;
    let newAlertMsg = alertMsg;
    if (!!~Object.keys(newVal).indexOf(errorKey)) {
      newErrorKey = null;
      newAlertMsg = null;
    }
    const val = {
      ...this.state,
      ...newVal,
      errorKey: newErrorKey,
      alertMsg: newAlertMsg,
    };
    this.setState(val);
  }
  public getAlertMsg = () => {
    const { intl } = this.context;
    const { account, password, verifyCode } = this.state;
    let alertMsg = null;
    let errorKey = null;
    switch (true) {
      case isBlank(account): {
        alertMsg = intl.formatMessage(messages.accountAlertMsg);
        errorKey = 'account';
        break;
      }
      case isBlank(password): {
        alertMsg = intl.formatMessage(messages.pwdPlaceholder);
        errorKey = 'password';
        break;
      }
      case isBlank(verifyCode): {
        alertMsg = intl.formatMessage(messages.captchaAlertMsg);
        errorKey = 'verifyCode';
        break;
      }
      default: {
        // 版权方登录无供应商选择，字段校验通过即提交
      }
    }
    this.setState({ alertMsg, errorKey });
    return alertMsg;
  }
  getRefreshRef = (refresh: any) => {
    this.refreshRef = refresh;
  }
  downloadHelp = () => {
    ajax({
      url: 'outsource/download?cate=help',
      method: 'POST',
      dataType: 'blob',
    });
  }

  public render() {
    const { intl } = this.context;
    const { prefixCls } = this.props;
    const { account, password, alertMsg, errorKey, loading } = this.state;
    return (
      <div className={`${prefixCls} ${prefixCls}-wrap`}>
        <div className={`${prefixCls}-copyright`}>
          {`Copyright 2017-${moment().year()} NetEase Inc All Rights Reserved`}
          <a
            className={`${prefixCls}-copyright-icp`}
            href="http://www.beian.miit.gov.cn"
            target="_blank"
            rel="noopener noreferrer"
          >
            ICP备案：粤B2-20090191-2
          </a>
        </div>
        {/* 走查：左侧 logo 高度与字体一致（22px）；padding-left 66→46（减20）；logo 垂直居中后向下偏移 1px */}
        <div className={`${prefixCls} ${prefixCls}-title !text-color-1 !pl-[46px] pr-6 !bg-[length:auto_22px] !bg-[position:0_calc(50%_+_1px)]`}>
          {getOuterTitle()}
        </div>
        <div className={`${prefixCls} ${prefixCls}-picture !bg-[length:100%_auto] !bg-center`} />
        <div className={`${prefixCls} ${prefixCls}-form`}>
          <Input
            className={`${prefixCls}-form-item user-name ${
              errorKey === 'account' ? 'show-error' : ''
            }`}
            placeholder={intl.formatMessage(messages.accountPlaceholder)}
            prefix={<Icon name="user-o" />}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              this.handleChange({ account: e.target.value })
            }
            value={account}
          />
          <Input
            className={`${prefixCls}-form-item user-pwd ${
              errorKey === 'password' ? 'show-error' : ''
            }`}
            type="password"
            placeholder={intl.formatMessage(messages.pwdPlaceholder)}
            prefix={<Icon name="lock-o" />}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              this.handleChange({ password: e.target.value })
            }
            value={password}
          />
          <Captcha
            locale={intl.locale}
            onChange={this.handleChange}
            getRefreshRef={this.getRefreshRef}
          />
          <div className={`${prefixCls}-alert-msg`}>
            {alertMsg && <Icon name="exclamation-circle-o" />}
            {alertMsg}
          </div>
          <Button
            type="primary"
            className={`${prefixCls}-login-btn`}
            onClick={this.handleLogin}
            loading={loading}
          >
            {intl.formatMessage(messages.loginBtnMsg)}
          </Button>
          <div className={`${prefixCls}-footer`}>
            <Button link onClick={this.downloadHelp}>{intl.formatMessage(messages.helpDocument)}</Button>
            <Divider vertical />
            <Button link>{intl.formatMessage(messages.contactUsMsg)}</Button>
          </div>
        </div>
      </div>
    );
  }
}

export function mapDispatchToProps(dispatch: Dispatch<actions.AccountActions>) {
  return bindActionCreators(
    {
      ipCooperationSessionsLogin: actions.ipCooperationSessionsLogin,
      getUserInfo: actions.getUserInfo,
    },
    dispatch,
  );
}

export default connect(null, mapDispatchToProps)(IpLogin);
